"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { ArrowLeft, MessageCircle, Search, SquarePen, X } from "lucide-react";
import { sfx } from "@/lib/sfx";

type Person = { id: string; displayName: string };
type Conversation = {
  user: Person;
  lastMessage: { body: string; createdAt: string; mine: boolean };
  unread: number;
};
type Msg = { id: string; body: string; createdAt: string; mine: boolean };

type View = { name: "list" } | { name: "new" } | { name: "thread"; user: Person };

/** Other parts of the app can open a conversation with: window.dispatchEvent(new CustomEvent("cad:open-dm", { detail: { accountId, displayName } })) */
export const OPEN_DM_EVENT = "cad:open-dm";

const UNREAD_POLL_MS = 6000;
const LIST_POLL_MS = 5000;
const THREAD_POLL_MS = 3000;

function timeLabel(iso: string) {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function DirectMessages() {
  const pathname = usePathname();

  // The widget lives in the root layout, so it also renders on /login and the
  // community picker. There the API answers 401/400, so it hides itself, and tries
  // again after the next page change (i.e. once the person is signed in).
  const [available, setAvailable] = useState(true);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>({ name: "list" });
  const [unread, setUnread] = useState(0);

  useEffect(() => setAvailable(true), [pathname]);

  // ---- unread badge (always polling while available) --------------------------------
  const prevUnread = useRef<number | null>(null);
  const pollUnread = useCallback(async () => {
    try {
      const res = await fetch("/api/dm/unread", { cache: "no-store" });
      if (res.status === 401 || res.status === 400) {
        setAvailable(false);
        return;
      }
      if (!res.ok) return;
      const { unread: n } = (await res.json()) as { unread: number };
      if (prevUnread.current !== null && n > prevUnread.current) sfx.play("message");
      prevUnread.current = n;
      setUnread(n);
    } catch {
      // a missed poll is fine
    }
  }, []);

  useEffect(() => {
    if (!available) return;
    prevUnread.current = null;
    pollUnread();
    const t = setInterval(pollUnread, UNREAD_POLL_MS);
    return () => clearInterval(t);
  }, [available, pollUnread]);

  // ---- let other components open a conversation ---------------------------------------
  useEffect(() => {
    function onOpen(e: Event) {
      const d = (e as CustomEvent<{ accountId?: string; displayName?: string }>).detail;
      if (!d?.accountId) return;
      setView({ name: "thread", user: { id: d.accountId, displayName: d.displayName || "Player" } });
      setOpen(true);
    }
    window.addEventListener(OPEN_DM_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_DM_EVENT, onOpen);
  }, []);

  if (!available) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[900] flex flex-col items-end gap-2 print:hidden">
      {open && (
        <div
          role="dialog"
          aria-label="Direct messages"
          className="w-[22rem] max-w-[calc(100vw-2rem)] h-[28rem] max-h-[calc(100vh-6rem)] flex flex-col rounded-lg border border-cad-border bg-cad-panel text-cad-text shadow-2xl"
        >
          {view.name === "list" && (
            <ConversationList
              onOpen={(user) => setView({ name: "thread", user })}
              onNew={() => setView({ name: "new" })}
              onClose={() => setOpen(false)}
              onUnreadChanged={setUnread}
            />
          )}
          {view.name === "new" && (
            <NewMessage
              onBack={() => setView({ name: "list" })}
              onPick={(user) => setView({ name: "thread", user })}
              onClose={() => setOpen(false)}
            />
          )}
          {view.name === "thread" && (
            <Thread
              key={view.user.id}
              user={view.user}
              onBack={() => {
                setView({ name: "list" });
                pollUnread();
              }}
              onClose={() => setOpen(false)}
              onRead={pollUnread}
            />
          )}
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        title="Direct messages"
        aria-label="Direct messages"
        className="relative flex items-center justify-center w-12 h-12 rounded-full bg-cad-accent text-white shadow-lg hover:bg-blue-600"
      >
        <MessageCircle className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-600 text-[11px] font-bold leading-5 text-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
    </div>
  );
}

function Header({
  title,
  onBack,
  onClose,
  right
}: {
  title: string;
  onBack?: () => void;
  onClose: () => void;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-cad-border">
      {onBack && (
        <button onClick={onBack} aria-label="Back" className="text-gray-400 hover:text-cad-text">
          <ArrowLeft className="w-4 h-4" />
        </button>
      )}
      <h2 className="flex-1 truncate text-sm font-semibold">{title}</h2>
      {right}
      <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-cad-text">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function ConversationList({
  onOpen,
  onNew,
  onClose,
  onUnreadChanged
}: {
  onOpen: (u: Person) => void;
  onNew: () => void;
  onClose: () => void;
  onUnreadChanged: (n: number) => void;
}) {
  const [items, setItems] = useState<Conversation[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch("/api/dm", { cache: "no-store" });
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { conversations: Conversation[]; totalUnread: number };
        setItems(data.conversations);
        onUnreadChanged(data.totalUnread);
      } catch {
        // ignore
      }
    }
    load();
    const t = setInterval(load, LIST_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Header
        title="Messages"
        onClose={onClose}
        right={
          <button onClick={onNew} title="New message" aria-label="New message" className="text-gray-400 hover:text-cad-text">
            <SquarePen className="w-4 h-4" />
          </button>
        }
      />
      <div className="flex-1 overflow-y-auto divide-y divide-cad-border">
        {items === null && <p className="p-4 text-xs text-gray-500">Loading...</p>}
        {items?.length === 0 && (
          <div className="p-4 text-sm text-gray-500">
            No conversations yet.{" "}
            <button onClick={onNew} className="text-cad-accent hover:underline">
              Start one
            </button>
            .
          </div>
        )}
        {items?.map((c) => (
          <button
            key={c.user.id}
            onClick={() => onOpen(c.user)}
            className="w-full text-left px-3 py-2.5 hover:bg-white/5 flex items-center gap-3"
          >
            <span className="flex items-center justify-center w-9 h-9 rounded-full bg-black/30 border border-cad-border text-sm font-semibold text-gray-300 shrink-0">
              {c.user.displayName.slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline justify-between gap-2">
                <span className={`truncate text-sm ${c.unread ? "font-semibold" : "font-medium"}`}>{c.user.displayName}</span>
                <span className="text-[10px] text-gray-600 shrink-0">{timeLabel(c.lastMessage.createdAt)}</span>
              </span>
              <span className={`block truncate text-xs ${c.unread ? "text-gray-200" : "text-gray-500"}`}>
                {c.lastMessage.mine ? "You: " : ""}
                {c.lastMessage.body}
              </span>
            </span>
            {c.unread > 0 && (
              <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-cad-accent text-[11px] font-bold leading-5 text-center text-white shrink-0">
                {c.unread}
              </span>
            )}
          </button>
        ))}
      </div>
    </>
  );
}

function NewMessage({
  onBack,
  onPick,
  onClose
}: {
  onBack: () => void;
  onPick: (u: Person) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Person[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/dm/users?q=${encodeURIComponent(term)}`);
        if (!res.ok || cancelled) return;
        setResults((await res.json()).users as Person[]);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q]);

  return (
    <>
      <Header title="New message" onBack={onBack} onClose={onClose} />
      <div className="p-3 border-b border-cad-border">
        <div className="flex items-center gap-2 rounded border border-cad-border bg-black/30 px-2">
          <Search className="w-4 h-4 text-gray-500" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a player by name..."
            className="flex-1 bg-transparent py-1.5 text-sm outline-none"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto divide-y divide-cad-border">
        {q.trim().length < 2 && <p className="p-4 text-xs text-gray-500">Type at least 2 letters.</p>}
        {q.trim().length >= 2 && !searching && results.length === 0 && (
          <p className="p-4 text-xs text-gray-500">No players found in this community.</p>
        )}
        {results.map((u) => (
          <button key={u.id} onClick={() => onPick(u)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-white/5">
            {u.displayName}
          </button>
        ))}
      </div>
    </>
  );
}

function Thread({
  user,
  onBack,
  onClose,
  onRead
}: {
  user: Person;
  onBack: () => void;
  onClose: () => void;
  onRead: () => void;
}) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastIdRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  const poll = useCallback(async () => {
    try {
      const url = lastIdRef.current
        ? `/api/dm/${user.id}?after=${lastIdRef.current}`
        : `/api/dm/${user.id}`;
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok || cancelledRef.current) return;
      const incoming = ((await res.json()) as { messages: Msg[] }).messages;
      if (incoming.length === 0 || cancelledRef.current) return;
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...prev, ...incoming.filter((m) => !seen.has(m.id))];
      });
      lastIdRef.current = incoming[incoming.length - 1].id;
      onRead(); // opening / receiving in an open thread marks it read server-side
    } catch {
      // ignore
    }
  }, [user.id, onRead]);

  useEffect(() => {
    cancelledRef.current = false;
    poll();
    const t = setInterval(poll, THREAD_POLL_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(t);
    };
  }, [poll]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`/api/dm/${user.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Couldn't send that message.");
        return;
      }
      setDraft("");
      poll();
    } catch {
      setError("Network error.");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <Header title={user.displayName} onBack={onBack} onClose={onClose} />
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5">
        {messages.length === 0 && <p className="text-xs text-gray-600">No messages yet. Say hello.</p>}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-sm break-words whitespace-pre-wrap ${
                m.mine ? "bg-cad-accent text-white" : "bg-black/30 border border-cad-border text-gray-200"
              }`}
            >
              {m.body}
              <span className={`block text-[10px] mt-0.5 ${m.mine ? "text-white/70" : "text-gray-600"}`}>
                {timeLabel(m.createdAt)}
              </span>
            </div>
          </div>
        ))}
      </div>
      {error && <p className="px-3 pb-1 text-xs text-red-400">{error}</p>}
      <form onSubmit={send} className="border-t border-cad-border p-2 flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={1000}
          placeholder="Message..."
          className="flex-1 bg-black/30 border border-cad-border rounded px-2 py-1.5 text-sm"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="bg-cad-accent hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-medium rounded px-3 py-1.5"
        >
          Send
        </button>
      </form>
    </>
  );
}
